import React, { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import useFirebasePreferences from "../hooks/useFirebasePreferences";
import { calculateWeight, GlassShape } from "../helpers/weightCalculator";
import type { LengthUnit } from "../types";

const shapes: { value: GlassShape; label: string }[] = [
  { value: GlassShape.Square, label: "Square" },
  { value: GlassShape.Rectangular, label: "Rectangle" },
  { value: GlassShape.Circular, label: "Circle" },
  { value: GlassShape.Oval, label: "Oval" },
  { value: GlassShape.Wedge, label: "Wedge" },
];

const GlassWeightCalculatorPage = () => {
  const preferences = useFirebasePreferences();
  const unit: LengthUnit = preferences?.length_unit ?? "mm";

  const [shape, setShape] = useState<GlassShape>(GlassShape.Square);
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [thickness, setThickness] = useState("");
  const [thickness2, setThickness2] = useState("");

  const unitLabel = unit === "in" ? "in" : "mm";

  const needsHeight =
    shape === GlassShape.Rectangular ||
    shape === GlassShape.Oval ||
    shape === GlassShape.Wedge;

  const needsThickness2 = shape === GlassShape.Wedge;

  const widthLabel = (() => {
    switch (shape) {
      case GlassShape.Square:
        return "Side";
      case GlassShape.Circular:
        return "Diameter";
      default:
        return "Width";
    }
  })();

  const heightLabel = shape === GlassShape.Oval ? "Height" : "Height";

  const weightGrams = useMemo(() => {
    const w = parseFloat(width);
    const t = parseFloat(thickness);
    if (isNaN(w) || isNaN(t) || w <= 0 || t <= 0) return null;

    if (needsHeight) {
      const h = parseFloat(height);
      if (isNaN(h) || h <= 0) return null;
    }

    if (needsThickness2) {
      const t2 = parseFloat(thickness2);
      if (isNaN(t2) || t2 <= 0) return null;
    }

    return calculateWeight(
      shape,
      unit,
      w,
      t,
      needsHeight ? parseFloat(height) : undefined,
      needsThickness2 ? parseFloat(thickness2) : undefined,
    );
  }, [shape, unit, width, height, thickness, thickness2, needsHeight, needsThickness2]);

  return (
    <div>
      <h1 className="text-3xl font-bold my-4">Glass Weight Calculator</h1>

      <div className="flex flex-wrap gap-2 mb-6">
        {shapes.map((s) => (
          <Button
            key={s.value}
            variant={shape === s.value ? "default" : "outline"}
            onClick={() => setShape(s.value)}
          >
            {s.label}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mb-6">
        <div>
          <Label htmlFor="width">{widthLabel} ({unitLabel})</Label>
          <Input
            id="width"
            type="number"
            min="0"
            step="any"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
          />
        </div>

        {needsHeight && (
          <div>
            <Label htmlFor="height">{heightLabel} ({unitLabel})</Label>
            <Input
              id="height"
              type="number"
              min="0"
              step="any"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
            />
          </div>
        )}

        <div>
          <Label htmlFor="thickness">
            {needsThickness2 ? "Thickness 1" : "Thickness"} ({unitLabel})
          </Label>
          <Input
            id="thickness"
            type="number"
            min="0"
            step="any"
            value={thickness}
            onChange={(e) => setThickness(e.target.value)}
          />
        </div>

        {needsThickness2 && (
          <div>
            <Label htmlFor="thickness2">Thickness 2 ({unitLabel})</Label>
            <Input
              id="thickness2"
              type="number"
              min="0"
              step="any"
              value={thickness2}
              onChange={(e) => setThickness2(e.target.value)}
            />
          </div>
        )}
      </div>

      {weightGrams !== null && (
        <div className="rounded-lg border p-6 max-w-md">
          <p className="text-4xl font-bold">{weightGrams.toFixed(1)} g</p>
          <p className="text-2xl text-muted-foreground mt-1">
            {(weightGrams * 0.035274).toFixed(2)} oz
          </p>
        </div>
      )}
    </div>
  );
};

export default GlassWeightCalculatorPage;
